import * as THREE from 'three';
import { box, weld } from '../level/VoxelFactory.js';
import { eventBus, Events } from '../core/EventBus.js';
import { gameState } from '../core/GameState.js';
import { CURRY, ROW_DEPTH, TILE_X, FIELD_HALF_TILES } from '../core/Constants.js';

function buildCurryBowlGeometry() {
  const BOWL_BASE = 0x78350f;   // Deep clay brown bowl base
  const BOWL_RIM = 0x92400e;    // Terracotta bowl rim
  const CURRY_GOLD = 0xf59e0b;  // Rich golden curry sauce
  const CHILI_RED = 0xdc2626;   // Red chili garnish
  const RICE_WHITE = 0xfef08a;  // Fragrant yellow jasmine rice

  const parts = [
    // Bowl base & sides
    box(1.3, 0.2, 1.3, BOWL_BASE, 0, 0.1, 0),
    box(1.5, 0.45, 1.5, BOWL_RIM, 0, 0.4, 0),
    // Curry sauce center
    box(1.25, 0.35, 1.25, CURRY_GOLD, 0, 0.5, 0),
    // Rice mound side
    box(0.5, 0.2, 0.5, RICE_WHITE, -0.3, 0.65, -0.2),
    // Red chili flakes & garnish
    box(0.22, 0.12, 0.22, CHILI_RED, 0.2, 0.7, 0.2),
    box(0.18, 0.12, 0.18, CHILI_RED, -0.1, 0.7, 0.3),
    box(0.2, 0.12, 0.2, CHILI_RED, 0.25, 0.7, -0.25),
  ];

  // Floating steam voxel particles above the bowl
  parts.push(box(0.15, 0.25, 0.15, 0xffffff, -0.2, 1.0, 0));
  parts.push(box(0.15, 0.25, 0.15, 0xffffff, 0.2, 1.15, 0.1));
  parts.push(box(0.15, 0.25, 0.15, 0xffffff, 0, 1.3, -0.1));

  return weld(parts);
}

export class CurryItem {
  constructor(scene, material, shadowMaterial) {
    this.scene = scene;
    this.material = material;
    this.shadowMaterial = shadowMaterial;

    this.geometry = buildCurryBowlGeometry();
    this.mesh = new THREE.Mesh(this.geometry, material);

    this.shadowGeometry = new THREE.BoxGeometry(1.4, 0.04, 1.4);
    this.shadow = new THREE.Mesh(this.shadowGeometry, shadowMaterial);
    this.shadow.renderOrder = 1;

    this.group = new THREE.Group();
    this.group.add(this.mesh);
    scene.add(this.group);
    scene.add(this.shadow);

    this.x = 0;
    this.row = 0;
    this.collected = true;
    this.hide();
  }

  spawn(x, row) {
    this.x = x;
    this.row = row;
    this.collected = false;

    const z = -row * ROW_DEPTH;
    this.group.position.set(x, 0.2, z);
    this.shadow.position.set(x, 0.02, z);

    this.show();
  }

  hide() {
    this.group.visible = false;
    this.shadow.visible = false;
  }

  show() {
    this.group.visible = true;
    this.shadow.visible = true;
  }

  update(dt, elapsed) {
    if (this.collected || !this.group.visible) return;

    // Bobbing & slow rotation animation
    this.mesh.rotation.y += dt * 1.8;
    this.mesh.position.y = 0.2 + Math.sin(elapsed * 4.5) * 0.15;

    // Check collision with player
    const p = gameState.player;
    if (!p || p.dead) return;

    if (p.row === this.row && Math.abs(p.x - this.x) < 1.1) {
      this.collect();
    }
  }

  collect() {
    if (this.collected) return;
    this.collected = true;
    this.hide();

    const roll = Math.random();
    const immunityProb = CURRY.IMMUNITY_CHANCE ?? 0.9;
    const isImmunity = roll < immunityProb;

    if (isImmunity) {
      const minD = CURRY.IMMUNITY_DUR_MIN ?? 3.0;
      const maxD = CURRY.IMMUNITY_DUR_MAX ?? 5.0;
      const dur = minD + Math.random() * (maxD - minD);

      gameState.player.spicyImmunityUntil = gameState.time + dur;
      eventBus.emit('curry:immunity', { duration: dur });
    } else {
      const minD = CURRY.POISON_DUR_MIN ?? 3.0;
      const maxD = CURRY.POISON_DUR_MAX ?? 5.0;
      const dur = minD + Math.random() * (maxD - minD);

      gameState.player.spicyImmunityUntil = 0;
      gameState.player.curryPoisonUntil = gameState.time + dur;
      eventBus.emit('curry:poison', { duration: dur });
    }
  }

  reset() {
    this.collected = true;
    this.hide();
  }

  dispose() {
    this.hide();
    this.group.removeFromParent();
    this.shadow.removeFromParent();
    this.geometry.dispose();
    this.shadowGeometry.dispose();
  }
}
